import type { FrontendSentryConfig } from './sentry.js';

export interface FrontendObservabilityConfig {
  readonly sentry: FrontendSentryConfig | null;
}

function parseSentryConfig(value: unknown): FrontendSentryConfig | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  if (typeof record.dsn !== 'string' || record.dsn === '') return null;
  return {
    dsn: record.dsn,
    ...(typeof record.environment === 'string' ? { environment: record.environment } : {}),
    ...(typeof record.release === 'string' ? { release: record.release } : {}),
  };
}

/**
 * Best-effort fetch of the runtime observability config. Every failure path
 * (dev 404, offline, malformed body) resolves to disabled rather than throwing —
 * reporting must never be able to break app startup.
 */
export async function fetchObservabilityConfig(
  fetchImpl: typeof fetch = fetch,
): Promise<FrontendObservabilityConfig> {
  try {
    const response = await fetchImpl('/api/observability/config', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) return { sentry: null };
    const payload = await response.json() as unknown;
    if (typeof payload !== 'object' || payload === null) return { sentry: null };
    return { sentry: parseSentryConfig((payload as Record<string, unknown>).sentry) };
  } catch {
    return { sentry: null };
  }
}
